import React, { useState } from "react";
import { useResolvedPath, useLocation, useNavigate } from "react-router-dom";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  SxProps,
  Theme,
} from "@mui/material";
import { ExpandLess, NavigateNext, ExpandMore } from "@mui/icons-material";
import { grey } from "@mui/material/colors";

import { If } from "@/features/shared/components";

const ITEM_HEIGHT = 52;

interface Props {
  children?: React.ReactNode;
  onClick?: () => void;
  icon?: React.ReactNode;
  arrow?: boolean;
  name: string;
  open?: boolean;
  route?: string;
  subText?: React.ReactNode;
  depth?: number;
  sx?: SxProps<Theme>;
}
export default function CollapsableListItem({
  children,
  icon,
  name,
  route,
  onClick,
  arrow,
  subText,
  sx,
  depth = 0,
  open = false,
}: Props) {
  const collapsable = React.Children.count(children) > 0;
  const location = useLocation();
  const resolve = useResolvedPath(route || "");
  const navigate = useNavigate();

  const active = route && location.pathname === resolve.pathname;

  const [collapsed, setCollapsed] = useState(!(active || open));

  const toggleCollapse = () => {
    setCollapsed((val) => !val);
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
      return;
    }

    if (route) {
      if (collapsable) setCollapsed(false);
      navigate(route);
    } else if (collapsable) {
      toggleCollapse();
    }
  };

  return (
    <>
      <ListItem
        disablePadding
        sx={{
          pl: depth > 0 ? 1.5 : 0,
          alignItems: "stretch",
          ...sx,
        }}
      >
        <ListItemButton
          selected={!!active}
          sx={{ py: 0, minHeight: ITEM_HEIGHT }}
          onClick={handleClick}
        >
          {depth <= 1 ? (
            <ListItemIcon sx={{ minWidth: 40 }}>{icon}</ListItemIcon>
          ) : (
            <>
              <Box
                sx={{
                  borderLeft: "2px dotted lightgrey",
                  height: ITEM_HEIGHT - 2,
                  mr: 2,
                  ml: 2,
                }}
              />
              <ListItemIcon sx={{ minWidth: 40 }}>{icon}</ListItemIcon>
            </>
          )}

          <ListItemText
            primary={name}
            secondary={subText}
            secondaryTypographyProps={{ component: "div" }}
          />

          <If condition={!collapsable && arrow}>
            <NavigateNext sx={{ color: grey[500] }} />
          </If>

          <If condition={collapsable}>
            {collapsed ? (
              <ExpandMore sx={{ color: grey[500] }} />
            ) : (
              <ExpandLess sx={{ color: grey[500] }} />
            )}
          </If>
        </ListItemButton>
      </ListItem>

      <If condition={collapsable}>
        <Collapse in={!collapsed}>
          <List component="div" disablePadding>
            {children}
          </List>
        </Collapse>
      </If>
    </>
  );
}
